/**
 * useAsyncAction — single, opinionated contract for every user-triggered
 * async action in the app. Wave 2 reliability sweep (Apr 30, 2026).
 *
 * Why it exists:
 *   - Every action button across the app must follow the same five rules:
 *       1. Show loading state immediately
 *       2. Block double-clicks while busy (idempotency at the UI layer)
 *       3. Always release the busy flag, even on error (no frozen buttons)
 *       4. Surface a real error toast — no silent failures
 *       5. Refetch / invalidate state on success so the UI never needs a
 *          manual refresh
 *
 * Usage:
 *   const { run, busy } = useAsyncAction();
 *   const onClickAccept = () => run(
 *     () => axios.post(`${API}/api/bookings/${id}/accept`, {}, { withCredentials: true }),
 *     {
 *       onSuccess: () => { toast.success("Accepted"); refetchBookings(); },
 *       errorMessage: "Couldn't accept the request",
 *     },
 *   );
 *   <Button disabled={busy} onClick={onClickAccept}>Accept</Button>
 *
 * Notes:
 *   - `busy` is per-hook-instance. For per-row busy state, instantiate per row
 *     OR use the `busyKey` argument and inspect `busyKeys.has(key)`.
 *   - Errors from FastAPI HTTPException get unpacked from `err.response.data.detail`
 *     so the toast shows the real backend reason ("Cancellations within 72 hours…").
 *   - Idempotent: a second call while busy is a no-op (returns Promise.resolve).
 */
import { useCallback, useState, useRef } from "react";
import { toast } from "sonner";

function extractErrorMessage(err, fallback) {
  if (!err) return fallback;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0 && detail[0]?.msg) return detail[0].msg;
  if (err?.message) return err.message;
  return fallback;
}

export default function useAsyncAction() {
  const [busy, setBusy] = useState(false);
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const inflightRef = useRef(false);

  const run = useCallback(async (fn, opts = {}) => {
    const {
      onSuccess,
      onError,
      successMessage,                        // optional auto-toast on success
      errorMessage = "Something went wrong — please try again.",
      busyKey,
    } = opts;

    // Per-key idempotency for action lists (e.g. row-level Accept/Decline).
    if (busyKey) {
      if (busyKeys.has(busyKey)) return undefined;
      setBusyKeys((s) => {
        const next = new Set(s);
        next.add(busyKey);
        return next;
      });
    } else {
      if (inflightRef.current) return undefined;
      inflightRef.current = true;
      setBusy(true);
    }

    try {
      const result = await fn();
      if (onSuccess) await onSuccess(result?.data ?? result, result);
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (err) {
      const msg = extractErrorMessage(err, errorMessage);
      if (onError) {
        const handled = await onError(err, msg);
        if (handled !== true) toast.error(msg);
      } else {
        toast.error(msg);
      }
      // Re-throw so callers using try/catch can react.
      throw err;
    } finally {
      if (busyKey) {
        setBusyKeys((s) => {
          const next = new Set(s);
          next.delete(busyKey);
          return next;
        });
      } else {
        inflightRef.current = false;
        setBusy(false);
      }
    }
  }, [busyKeys]);

  const isBusy = useCallback((key) => (key ? busyKeys.has(key) : busy), [busy, busyKeys]);

  // `loading` is an alias of `busy` so callers can adopt either naming.
  return { run, busy, loading: busy, isBusy, busyKeys };
}
